import {
  all, call, put, select, takeLatest,
} from 'redux-saga/effects';
import { request } from 'utils/request';
import {
  clearAuthToken, setAuthToken, setRefreshToken,
} from 'helpers/token';
import { IRestApiResponse } from 'interfaces';
import { CoreSliceActions } from 'base/core/slice';
import { Role } from 'utils/user-roles';
import { capitalize } from 'lodash';
import { CategoryResourceType } from 'constants/sessions';
import { authSliceActions } from './slice';
import API from './constants';
import {
  IForgotPasswordAction,
  IResetPasswordAction,
  IResetTempPasswordAction,
  ISignInAction,
  IUserRole,
} from './interface';
import { selectCallbackPath } from './selectors';

export function* signInGenerator({ payload }: ISignInAction): any {
  try {
    const response: IRestApiResponse = yield call(request, API.POST_SIGN_IN, payload, false);
    if (response.statusCode === 201) {
      setAuthToken(response.data.idToken.jwtToken);
      setRefreshToken(response.data.refreshToken.token);
      yield put(authSliceActions.signInSucceeded({ idToken: response.data.idToken }));
    } else {
      yield clearAuthToken();
      yield put(
        CoreSliceActions.setMessage({
          message: response?.message ? capitalize(response?.message) : 'Authentication failed',
          success: false,
        }),
      );
      yield put(authSliceActions.signInFailed(capitalize(response?.message) ?? 'Authentication failed'));
    }
  } catch (error) {
    yield put(
      CoreSliceActions.setMessage({
        message: 'Authentication failed',
        success: false,
      }),
    );
    yield put(authSliceActions.signInFailed('Authentication failed'));
  }
}

function* getCategories(resourceId: string) {
  const response: IRestApiResponse = yield call(
    request,
    {
      path: `${API.GET_CATEGORIES.path}?resourceId=${resourceId}&resourceType=${CategoryResourceType.EXPERT}`,
      method: API.GET_CATEGORIES.method,
    },
    null,
    true,
  );

  return response;
}

export function* authFetchUserGenerator() {
  try {
    const response: IRestApiResponse = yield call(request, API.GET_USER_DETAILS, null, true);
    if (response.statusCode === 200) {
      if (response.data.roles.find((r: IUserRole) => r.role === Role.Expert)) {
        const categories: IRestApiResponse = yield getCategories(response.data.id);
        yield put(authSliceActions.fetchUserSucceeded({
          ...response.data,
          categories: categories.data,
        }));
        yield put(authSliceActions.setStripeConnectAlertOpen());
        const callbackPath: string = yield select(selectCallbackPath);
        yield (window.location.href = callbackPath ?? '/');
      } else {
        yield clearAuthToken();
        yield put(
          CoreSliceActions.setMessage({
            message: 'Authorization failed',
            success: false,
          }),
        );
      }
    } else {
      yield put(
        CoreSliceActions.setMessage({
          message: 'Authentication failed',
          success: false,
        }),
      );
    }
  } catch (err) {
    yield put(
      CoreSliceActions.setMessage({
        message: 'Authentication failed',
        success: false,
      }),
    );
  }
}

export function* fetchProfileGenerator() {
  try {
    const response: IRestApiResponse = yield call(request, API.GET_USER_DETAILS, null, true);
    if (response.statusCode === 200) {
      const categories: IRestApiResponse = yield getCategories(response.data.id);
      yield put(authSliceActions.fetchUserSucceeded({
        ...response.data,
        categories: categories.data,
      }));
    }
  } catch (err) {
    yield put(authSliceActions.fetchUserFailed('User request failed'));
  }
}

export function* authSignOutUserGenerator() {
  try {
    yield call(request, API.POST_SIGN_OUT, {}, true);
    yield clearAuthToken();
    yield put(authSliceActions.signOutSucceeded());
  } catch (err) {
    yield put(authSliceActions.signOutFailed('User request failed'));
  }
}

export function* getCodeGenerator({ payload }: IForgotPasswordAction) {
  try {
    const response: IRestApiResponse = yield call(
      request,
      API.POST_FORGOT_PASSWORD,
      { email: payload.name },
      false,
    );
    if (!response) {
      yield payload.reject();
    } else {
      yield put(authSliceActions.getCodeSucceeded());
      yield payload.resolve();
    }
  } catch (error) {
    yield put(authSliceActions.getCodeFailed('Authorization failed'));
    yield payload.reject();
  }
}

export function* resetPasswordGenerator({ payload }: IResetPasswordAction) {
  try {
    const response: IRestApiResponse = yield call(request, API.POST_RESET_PASSWORD, {
      email: payload.name,
      token: payload.verificationCode,
      newPassword: payload.confirmPassword,
    }, false);
    if (response.statusCode === 201) {
      yield put(authSliceActions.resetPasswordSucceeded(response));
      payload.resolve();
    } else {
      yield put(authSliceActions.resetPasswordFailed('Password reset failed'));
      payload.reject();
    }
  } catch (error) {
    payload.reject();
    yield put(authSliceActions.resetPasswordFailed('Password reset failed'));
  }
}

export function* resetTempPasswordGenerator({ payload }: IResetTempPasswordAction) {
  try {
    const response: IRestApiResponse = yield call(
      request,
      API.POST_RESET_TEMP_PASSWORD,
      payload,
      false,
    );
    if (response.statusCode === 201) {
      setAuthToken(response.data.idToken.jwtToken);
      setRefreshToken(response.data.refreshToken.token);
      yield put(
        authSliceActions.resetTempPasswordSucceeded({
          idToken: response.data.idToken,
        }),
      );
    }
  } catch (error) {
    yield put(authSliceActions.resetTempPasswordFailed('Authorization failed'));
  }
}

export function* generateConnectUrlGenerator({ payload }: {
  payload: { resolve: any; reject: any };
  type: string;
}): any {
  const data: any = {
    returnRoute: 'profile/update',
  };
  const response: IRestApiResponse = yield call(request, API.GENERATE_CONNECT_URL, data, true);
  if (response.statusCode === 201) {
    yield put(authSliceActions.generateConnectUrlSucceeded(response.data));
    yield call(payload.resolve);
  } else {
    yield put(authSliceActions.generateConnectUrlFailed());
    yield call(payload.reject);
  }
}

export function* authSaga() {
  yield all([
    takeLatest(authSliceActions.signIn.type, signInGenerator),
    takeLatest(authSliceActions.signInSucceeded.type, authFetchUserGenerator),
    takeLatest(authSliceActions.fetchUser.type, fetchProfileGenerator),
    takeLatest(authSliceActions.signOut.type, authSignOutUserGenerator),
    takeLatest(authSliceActions.resetPassword.type, resetPasswordGenerator),
    takeLatest(authSliceActions.resetTempPassword.type, resetTempPasswordGenerator),
    takeLatest(authSliceActions.resetTempPasswordSucceeded.type, fetchProfileGenerator),
    takeLatest(authSliceActions.getCode.type, getCodeGenerator),
    takeLatest(authSliceActions.generateConnectUrl.type, generateConnectUrlGenerator),
  ]);
}
export default authSaga;
